import React, { useState } from 'react';
import { GlobalButton } from './GlobalButton';
import { Toast } from './Toast';
import ContentGrid from './ContentGrid';
import { useTheme } from '../context/ThemeProvider';
import { useLocale } from '../context/DynamicLocaleProvider';
import { useProjects } from '../hooks/useProjects';
import { collection, addDoc, doc, updateDoc, arrayUnion, query, where, getDocs } from 'firebase/firestore';
import styles from './EmployeeImportPreview.module.css';
import { db } from '../lib/firebase';

interface EmployeeData {
  id: string;
  salutation?: string;
  title?: string;
  firstName: string;
  secondName: string;
  email?: string;
  project?: string;
  role: string;
  status: 'valid' | 'error' | 'warning';
  errors: string[];
  warnings: string[];
}

interface EmployeeImportPreviewProps {
  employees: EmployeeData[];
  onImportComplete: (importedCount: number) => void;
  onCancel: () => void;
  inviterId: string;
  inviterName: string;
  inviterEmail: string;
  companyId?: string;
  selectedRole?: string;
  selectedProject?: string; // Can be projectId or project name (for backward compatibility)
}

export default function EmployeeImportPreview({
  employees,
  onImportComplete,
  onCancel,
  inviterId,
  inviterName,
  inviterEmail,
  companyId,
  selectedRole = 'client_admin',
  selectedProject = ''
}: EmployeeImportPreviewProps) {
  const { theme } = useTheme();
  const { t } = useLocale();
  const { projects } = useProjects(true);
  const isDarkMode = theme === 'dark';
  const [editableEmployees, setEditableEmployees] = useState<EmployeeData[]>(employees);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [sendEmailInvitations, setSendEmailInvitations] = useState(false);

  // Helper function to resolve project: if it's a projectId, use it; if it's a name, find matching project
  const resolveProjectId = (projectValue?: string): string | null => {
    if (!projectValue) return null;
    
    // First, check if it's already a valid projectId
    const projectById = projects.find(b => b.id === projectValue);
    if (projectById) return projectById.id;
    
    // Otherwise, try to find by name
    const projectByName = projects.find(b => 
      b.projectName.toLowerCase() === projectValue.toLowerCase() ||
      b.projectName.toLowerCase().includes(projectValue.toLowerCase())
    );
    if (projectByName) return projectByName.id;
    
    return null;
  };

  // Simple validation - just check for empty fields
  const validateEmployee = (employee: EmployeeData): { status: 'valid' | 'error' | 'warning', errors: string[], warnings: string[] } => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Just check if fields are empty - no complex validation
    if (!employee.firstName?.trim()) errors.push(t('employee_import_preview_first_name_empty_error'));
    if (!employee.secondName?.trim()) errors.push(t('employee_import_preview_last_name_empty_error'));

    const status = errors.length > 0 ? 'error' : 'valid';
    return { status, errors, warnings };
  };

  // Update employee data
  const updateEmployee = (id: string, field: keyof EmployeeData, value: string) => {
    setEditableEmployees(prev => prev.map(emp => {
      if (emp.id === id) {
        const updated = { ...emp, [field]: value };
        const validation = validateEmployee(updated);
        return { ...updated, ...validation };
      }
      return emp;
    }));
  };

  // Add new row
  const addNewRow = () => {
    const newEmployee: EmployeeData = {
      id: `new-${Date.now()}`,
      salutation: '',
      title: '',
      firstName: '',
      secondName: '',
      email: '',
      project: selectedProject,
      role: selectedRole,
      status: 'error',
      errors: [t('employee_import_preview_first_name_empty_error'), t('employee_import_preview_last_name_empty_error')],
      warnings: []
    };
    setEditableEmployees(prev => [...prev, newEmployee]);
  };

  // Remove selected rows
  const removeSelectedRows = () => {
    setEditableEmployees(prev => prev.filter(emp => !selectedRows.has(emp.id)));
    setSelectedRows(new Set());
  };

  // Toggle row selection
  const toggleRowSelection = (id: string) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Select all valid rows
  const selectAllValid = () => {
    const validIds = editableEmployees
      .filter(emp => emp.status === 'valid')
      .map(emp => emp.id);
    setSelectedRows(new Set(validIds));
  };

  // Import selected employees
  const handleImport = async () => {
    const toTrimmedString = (value: unknown): string => {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string') return value.trim();
      return String(value).trim();
    };
    const validEmployees = editableEmployees.filter(emp =>
      selectedRows.has(emp.id) && emp.firstName?.trim() && emp.secondName?.trim()
    );

    if (validEmployees.length === 0) {
      setError(t('employee_import_preview_import_error'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let importedCount = 0;

      for (const employee of validEmployees) {
        // Check if email already exists (if email is provided)
        if (toTrimmedString(employee.email)) {
          const emailToCheck = toTrimmedString(employee.email).toLowerCase();
          
          // Check Firestore for existing user with this email
          const usersQuery = query(
            collection(db, 'users'),
            where('email', '==', emailToCheck)
          );
          const existingUsers = await getDocs(usersQuery);
          
          if (!existingUsers.empty) {
            // Skip this employee and continue with next one
            console.warn(`Skipping employee ${employee.firstName} ${employee.secondName} - email ${emailToCheck} already exists`);
            continue;
          }
        }
        
        const userData: any = {
          firstName: employee.firstName.trim(),
          lastName: employee.secondName.trim(),
          role: employee.role,
          company: companyId,
          status: 'invited' as const,
          createdAt: new Date().getTime(),
          invitedBy: inviterId,
          invitedAt: new Date().getTime()
        };

        // Only add optional fields if they have values
        if (toTrimmedString(employee.salutation)) {
          userData.salutation = toTrimmedString(employee.salutation);
        }
        if (toTrimmedString(employee.title)) {
          userData.title = toTrimmedString(employee.title);
        }
        
        if (toTrimmedString(employee.email)) {
          userData.email = toTrimmedString(employee.email).toLowerCase(); // Normalize email to lowercase
        }
        
        // Handle project: resolve projectId and save project structure
        const projectValue = toTrimmedString(employee.project) || selectedProject;
        const resolvedProjectId = resolveProjectId(projectValue);
        
        if (resolvedProjectId) {
          userData.projectId = resolvedProjectId;
        } else if (projectValue) {
          // If we can't resolve it, still save the projectId as the value (for backward compatibility)
          // This handles cases where the project might be created later
          userData.projectId = projectValue;
        }

        // Add user to database
        const newUserRef = await addDoc(collection(db, 'users'), userData);

        // Update inviter's familyMembers
        const inviterRef = doc(db, 'users', inviterId);
        await updateDoc(inviterRef, {
          familyMembers: arrayUnion(newUserRef.id)
        });

        // Send email invitation if enabled and email is provided
        if (sendEmailInvitations && employee.email?.trim()) {
          try {
            const response = await fetch('https://sendinvitation-kahfqpgsrq-ey.a.run.app', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                email: employee.email.trim(),
                inviterId: inviterId,
                inviterName: inviterName,
                inviterEmail: inviterEmail,
                companyId: companyId,
                role: employee.role,
                language: 'en'
              }),
            });

            if (!response.ok) {
              console.warn(`Failed to send invitation email for ${employee.email}`);
            }
          } catch (emailError) {
            console.warn(`Failed to send invitation email for ${employee.email}:`, emailError);
          }
        }

        importedCount++;
      }

      setSuccess(t('employee_import_preview_import_success').replace('{count}', importedCount.toString()));

      setTimeout(() => {
        onImportComplete(importedCount);
      }, 1500);

    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Import failed:', err);
      }
      setError(t('employee_import_preview_import_failed_error'));
    } finally {
      setLoading(false);
    }
  };

  const validCount = editableEmployees.filter(emp => emp.firstName?.trim() && emp.secondName?.trim()).length;
  const selectedValidCount = editableEmployees.filter(emp =>
    selectedRows.has(emp.id) && emp.firstName?.trim() && emp.secondName?.trim()
  ).length;

  return (
    <div className={styles.modalOverlay}>
      <ContentGrid rows="auto 1fr auto">

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <h2>{t('employee_import_preview_title')}</h2>
            <p className={styles.subtitle}>
              {t('employee_import_preview_subtitle')
                .replace('{total}', editableEmployees.length.toString())
                .replace('{valid}', validCount.toString())
                .replace('{selected}', selectedValidCount.toString())}
            </p>
          </div>

          <div className={styles.toolbar}>

            {/* Cancel button */}
            <GlobalButton
              variant="secondary"
              onClick={() => onCancel()}
              disabled={loading}
              destructive={true}
            >
              {t('employee_import_preview_cancel_button')}
            </GlobalButton>

          </div>
        </div>

        {/* Table Content */}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.checkboxColumn}>
                  <input
                    type="checkbox"
                    checked={selectedRows.size === editableEmployees.length && editableEmployees.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRows(new Set(editableEmployees.map(emp => emp.id)));
                      } else {
                        setSelectedRows(new Set());
                      }
                    }}
                  />
                </th>
                <th className={styles.salutationColumn}>Anrede</th>
                <th className={styles.titleColumn}>Titel</th>
                <th className={styles.lastNameColumn}>Nachname</th>
                <th className={styles.firstNameColumn}>Vorname</th>
              </tr>
            </thead>
            <tbody>
              {editableEmployees.map((employee) => (
                <tr
                  key={employee.id}
                  className={`${styles.row} ${selectedRows.has(employee.id) ? styles.selected : ''}`}
                >
                  <td className={styles.checkboxColumn}>
                    <input
                      type="checkbox"
                      checked={selectedRows.has(employee.id)}
                      onChange={() => toggleRowSelection(employee.id)}
                    />
                  </td>
                  <td className={styles.salutationColumn}>
                    <input
                      type="text"
                      value={employee.salutation || ''}
                      onChange={(e) => updateEmployee(employee.id, 'salutation', e.target.value)}
                      className={styles.input}
                      placeholder={t('employee_import_preview_optional_placeholder')}
                    />
                  </td>
                  <td className={styles.titleColumn}>
                    <input
                      type="text"
                      value={employee.title || ''}
                      onChange={(e) => updateEmployee(employee.id, 'title', e.target.value)}
                      className={styles.input}
                      placeholder={t('employee_import_preview_optional_placeholder')}
                    />
                  </td>
                  <td className={styles.lastNameColumn}>
                    <input
                      type="text"
                      value={employee.secondName}
                      onChange={(e) => updateEmployee(employee.id, 'secondName', e.target.value)}
                      className={`${styles.input} ${employee.errors.some(e => e.includes('Last name')) ? styles.error : ''}`}
                    />
                  </td>
                  <td className={styles.firstNameColumn}>
                    <input
                      type="text"
                      value={employee.firstName}
                      onChange={(e) => updateEmployee(employee.id, 'firstName', e.target.value)}
                      className={`${styles.input} ${employee.errors.some(e => e.includes('First name')) ? styles.error : ''}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer Actions */}
        <div className={styles.footer}>

          {/* Add employee button */}
          <GlobalButton
            variant="secondary"
            onClick={addNewRow}
            disabled={loading}
            icon={<img src={ isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"} alt={t('ask_archive_sheet_ingest_knowledge_alt')} width={12} height={12} />}
          >
            {t('employee_import_preview_add_employee_button')}
          </GlobalButton>

          {/* Remove selected button */}
          {selectedRows.size > 0 && (
            <GlobalButton
              variant="secondary"
              onClick={removeSelectedRows}
              disabled={loading}
              icon={<img src={ isDarkMode ? "/assets/icons/trash.svg" : "/assets/icons/trash.black.svg"} alt={t('ask_archive_sheet_ingest_knowledge_alt')} width={12} height={12} />}
            >
              {t('employee_import_preview_remove_selected_button').replace('{count}', selectedRows.size.toString())}
            </GlobalButton>
          )}

          {/* Select all valid button */}
          <GlobalButton
            variant="secondary"
            onClick={selectAllValid}
            disabled={loading}
            icon={<img src={ isDarkMode ? "/assets/icons/checkmark.circle.svg" : "/assets/icons/checkmark.circle.black.svg"} alt={t('ask_archive_sheet_ingest_knowledge_alt')} width={12} height={12} />}
          >
            {t('employee_import_preview_select_all_valid_button')}
          </GlobalButton>

          {/* Email invitation checkbox */}
          <div className={styles.emailInvitationSection}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={sendEmailInvitations}
                onChange={(e) => setSendEmailInvitations(e.target.checked)}
                disabled={loading}
              />
              <span>{t('employee_import_preview_send_email_invitations')}</span>
            </label>
          </div>

          {/* Import button */}
          <GlobalButton
            onClick={handleImport}
            isLoading={loading}
            disabled={loading || selectedValidCount === 0}
          >
            {t('employee_import_preview_import_selected_button').replace('{count}', selectedValidCount.toString())}
          </GlobalButton>

        </div>
      </ContentGrid>

      {/* Error toast */}
      {error && (
        <Toast
          message={error}
          type="error"
          isVisible={true}
          onClose={() => setError(null)}
        />
      )}

      {/* Success toast */}
      {success && (
        <Toast
          message={success}
          type="success"
          isVisible={true}
          onClose={() => setSuccess(null)}
        />
      )}
    </div>
  );
}
